<template>
  <div>
    <div class="section-title d-flex justify-space-between align-center">
      <div>
        {{ $t("message.facilityDetails.moreHours.title") }}
      </div>
      <v-btn
        v-if="items.length > 0 && isOwner && canEditFacilityHours"
        icon
        color="primary"
        @click="dialog.categories = true"
        :disabled="isReadonly"
      >
        <v-icon>
          icon-edit
        </v-icon>
      </v-btn>
    </div>
    <ul v-if="items.length > 0" class="pl-0">
      <MoreHoursCategoryListItem
        class="more-hours__category-list-item"
        v-for="item in items"
        :key="item.id"
        :item="item"
      />
    </ul>
    <ui-button
      v-else-if="canEditFacilityHours"
      color="primary"
      icon="mdi-plus"
      outlined
      block
      @click="dialog.categories = true"
      :disabled="isReadonly"
    >
      {{ $t("message.common.add") }}
    </ui-button>

    <p v-else class="grey--text">{{ $t("message.common.noData") }}</p>

    <SectionLoader :status="loading" :opacity="0.5" />

    <MoreHoursCategoriesDialog
      :is-open="dialog.categories"
      :facilities-ids="facilitiesIds"
      :temp-values="tempValues"
      @setTempValues="tempValues = $event"
      @close="closeCategoriesDialog"
      @edit="handleEditCategory"
      @save="saveMoreHours"
    />

    <OpeningHoursDialog
      v-if="dialog.openingHours"
      :maxWidth="1154"
      :days="chosenCategory.values"
      :item="chosenCategory.info"
      :is-more-hours-version="true"
      @save="handleSaveCategoryHours"
      @close="closeOpeningHoursDialog"
    />
  </div>
</template>

<script>
import cuid from "cuid";
import { cloneDeep } from "lodash";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";

import SectionLoader from "@/components/Loader/SectionLoader.vue";
import MoreHoursCategoryListItem from "@/components/FacilityDetails/GmbMoreHours/MoreHoursCategoryListItem.vue";
import MoreHoursCategoriesDialog from "@/components/FacilityDetails/GmbMoreHours/MoreHoursCategoriesDialog.vue";
import OpeningHoursDialog from "@/components/FacilityDetails/Dialog/OpeningHoursDialog.vue";

export default {
  name: "MoreHours",
  data() {
    return {
      dialog: {
        categories: false,
        openingHours: false
      },
      tempValues: [],
      chosenCategory: null
    };
  },
  mixins: [permissionsInfo],
  components: {
    SectionLoader,
    MoreHoursCategoryListItem,
    MoreHoursCategoriesDialog,
    OpeningHoursDialog
  },
  props: {
    loading: {
      type: Boolean,
      required: true
    },
    isOwner: {
      type: Boolean,
      required: true
    },
    isReadonly: {
      type: Boolean,
      required: true
    },
    facilitiesIds: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    "dialog.categories"(newVal) {
      if (newVal && this.chosenCategory === null) {
        this.tempValues = cloneDeep(this.items);
      }
    }
  },
  methods: {
    closeCategoriesDialog() {
      this.dialog.categories = false;
    },
    async closeOpeningHoursDialog() {
      this.dialog.openingHours = false;

      await this.$nextTick(() => {
        this.dialog.categories = true;
      });

      await this.$nextTick(() => {
        this.chosenCategory = null;
      });
    },
    generateCategoryValues(values) {
      const generatedValues = {};

      const days = values || {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
      };

      for (const [key, value] of Object.entries(days)) {
        generatedValues[key] = {
          name: this.$t(`message.common.${key}`),
          id: cuid(),
          value
        };
      }

      return generatedValues;
    },

    handleEditCategory(category) {
      this.chosenCategory = {
        id: category.id || cuid(),
        info: { title: category.name, type: category.type },
        values: this.generateCategoryValues(category.values)
      };

      this.dialog.categories = false;
      this.dialog.openingHours = true;
    },

    handleSaveCategoryHours(hours) {
      const foundIndex = this.tempValues.findIndex(
        value => value.id === this.chosenCategory.id
      );

      if (foundIndex > -1) {
        Object.entries(hours).forEach(([k, v]) => {
          this.tempValues[foundIndex].values[k] = v.value;
        });
      } else {
        const formattedValues = {};
        Object.entries(hours).forEach(([k, v]) => {
          formattedValues[k] = v.value;
        });

        this.tempValues.push({
          id: cuid(),
          name: this.chosenCategory.info.title,
          type: this.chosenCategory.info.type,
          values: formattedValues
        });
      }
      this.closeOpeningHoursDialog();
    },

    saveMoreHours(values) {
      this.$emit("save", values);
      this.tempValues = [];
      this.closeCategoriesDialog();
    },

    setTempValues(values) {
      this.tempValues = values;
    }
  }
};
</script>

<style lang="scss" scoped>
.more-hours {
  &__category-list-item {
    &:not(:first-child) {
      margin-top: $spacing * 2;
    }
  }
}

.section-title {
  @include sectionTitle();
}
</style>
